import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Progress,
  Timeline,
  Button,
  Dropdown,
  Space,
} from 'antd';
import {
  DollarOutlined,
  CarOutlined,
  UserOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  CalendarOutlined,
  PhoneOutlined,
  MessageOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

interface KPICardProps {
  title: string;
  value: string | number;
  prefix?: React.ReactNode;
  suffix?: string;
  change?: number;
  changeType?: 'increase' | 'decrease';
  icon: React.ReactNode;
  color: string;
  trend?: number[];
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  prefix,
  suffix,
  change,
  changeType,
  icon,
  color,
}) => {
  return (
    <Card
      hoverable
      styles={{ body: { padding: '18px' } }}
      style={{
        background: `linear-gradient(135deg, ${color}08 0%, ${color}02 100%)`,
        borderTop: `4px solid ${color}`,
        borderRadius: '12px',
        border: 'none',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        height: '100%'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#6b7280',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {title}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
            <span style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#111827',
              letterSpacing: '-0.02em',
              lineHeight: '1.1'
            }}>
              {prefix}{value}{suffix}
            </span>
            {change && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600',
                backgroundColor: changeType === 'increase' ? '#dcfce7' : '#fee2e2',
                color: changeType === 'increase' ? '#166534' : '#991b1b'
              }}>
                <span>{changeType === 'increase' ? '↗' : '↘'}</span>
                <span>{change}%</span>
              </div>
            )}
          </div>
        </div>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: color,
          boxShadow: `0 4px 12px ${color}30`,
          flexShrink: 0
        }}>
          <span style={{ fontSize: '20px', color: 'white' }}>{icon}</span>
        </div>
      </div>
    </Card>
  );
};

interface ActivityItem {
  id: string;
  type: 'customer' | 'work-order' | 'estimate' | 'payment';
  title: string;
  description: string;
  time: string;
  avatar?: string;
  status?: 'success' | 'warning' | 'error' | 'info';
}

const recentActivities: ActivityItem[] = [
  {
    id: '1',
    type: 'customer',
    title: 'New customer registered',
    description: 'Sarah Johnson added her 2021 Honda Civic',
    time: '2 minutes ago',
    status: 'success',
  },
  {
    id: '2',
    type: 'work-order',
    title: 'Work order completed',
    description: 'Oil change for Mike\'s Toyota Camry - $45.00',
    time: '15 minutes ago',
    status: 'success',
  },
  {
    id: '3',
    type: 'estimate',
    title: 'Estimate sent',
    description: 'Brake repair estimate sent to Jennifer Wilson',
    time: '1 hour ago',
    status: 'info',
  },
  {
    id: '4',
    type: 'work-order',
    title: 'Work order overdue',
    description: 'Transmission repair for John\'s Ford F-150',
    time: '2 hours ago',
    status: 'warning',
  },
  {
    id: '5',
    type: 'payment',
    title: 'Payment received',
    description: 'Lisa Davis paid $320.00 for engine diagnostic',
    time: '3 hours ago',
    status: 'success',
  },
];

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState('today');

  const timeRangeMenuItems = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'quarter', label: 'This Quarter' },
  ];

  const getActivityIcon = (type: string, status?: string) => {
    switch (type) {
      case 'customer':
        return <UserOutlined className="text-blue-600" />;
      case 'work-order':
        return status === 'warning' ?
          <ExclamationCircleOutlined className="text-orange-500" /> :
          <FileTextOutlined className="text-green-600" />;
      case 'estimate':
        return <FileTextOutlined className="text-purple-600" />;
      case 'payment':
        return <DollarOutlined className="text-green-600" />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'success': return '#22c55e';
      case 'warning': return '#f59e0b';
      case 'error': return '#ef4444';
      case 'info': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f9fafb 0%, rgba(239, 246, 255, 0.2) 50%, rgba(243, 232, 255, 0.2) 100%)'
    }}>
      <div style={{
        padding: '16px',
        maxWidth: '1280px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'flex',
            flexDirection: window.innerWidth < 640 ? 'column' : 'row',
            alignItems: window.innerWidth < 640 ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: '16px'
          }}>
            <div style={{ position: 'relative' }}>
              <h1 style={{
                fontSize: window.innerWidth < 768 ? '28px' : '36px',
                fontWeight: 'bold',
                color: '#111827',
                marginBottom: '12px',
                lineHeight: '1.2'
              }}>
                Good morning, {user?.first_name || 'there'}! 👋
              </h1>
              <p style={{
                fontSize: window.innerWidth < 768 ? '16px' : '18px',
                color: '#6b7280',
                maxWidth: '500px',
                lineHeight: '1.6',
                margin: 0
              }}>
                Here's what's happening at your auto shop today. Stay on top of your business metrics.
              </p>
              <div style={{
                position: 'absolute',
                top: '-8px',
                left: '-8px',
                width: '80px',
                height: '80px',
                backgroundColor: 'rgba(59, 130, 246, 0.03)',
                borderRadius: '50%',
                filter: 'blur(20px)',
                zIndex: -1
              }}></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Dropdown menu={{ items: timeRangeMenuItems, selectedKeys: [timeRange], onClick: ({ key }) => setTimeRange(key) }} trigger={['click']}>
                <Button
                  icon={<CalendarOutlined />}
                  size="large"
                  style={{
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '0 24px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {timeRange === 'today' ? 'Today' :
                   timeRange === 'week' ? 'This Week' :
                   timeRange === 'month' ? 'This Month' : 'This Quarter'}
                </Button>
              </Dropdown>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{ width: '4px', height: '24px', background: 'linear-gradient(to bottom, #3b82f6, #8b5cf6)', borderRadius: '2px' }}></div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', margin: 0 }}>Key Performance Metrics</h2>
          </div>
          <Row gutter={[12, 16]}>
            <Col xs={24} sm={12} md={12} lg={6}>
              <KPICard
                title="Today's Revenue"
                value="2,847"
                prefix="$"
                change={12.5}
                changeType="increase"
                icon={<DollarOutlined />}
                color="#10b981"
              />
            </Col>
            <Col xs={24} sm={12} md={12} lg={6}>
              <KPICard
                title="Pending Estimates"
                value={4}
                change={25.0}
                changeType="increase"
                icon={<DollarOutlined />}
                color="#f59e0b"
              />
            </Col>
            <Col xs={24} sm={12} md={12} lg={6}>
              <KPICard
                title="Active Jobs"
                value={23}
                change={8.2}
                changeType="increase"
                icon={<FileTextOutlined />}
                color="#3b82f6"
              />
            </Col>
            <Col xs={24} sm={12} md={12} lg={6}>
              <KPICard
                title="New Customers"
                value={7}
                change={15.3}
                changeType="increase"
                icon={<UserOutlined />}
                color="#f59e0b"
              />
            </Col>
          </Row>
        </div>

        <Row gutter={[20, 24]}>
          {/* Work Orders Status */}
          <Col xs={24} lg={8}>
            <Card
              title={
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <FileTextOutlined className="text-white" />
                  </div>
                  <span className="font-semibold text-gray-800">Work Orders Status</span>
                </div>
              }
              style={{
                height: '100%',
                borderRadius: '16px',
                border: 'none',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
              }}
              styles={{ body: { padding: '20px' } }}
              extra={
                <Dropdown
                  menu={{ items: [
                    { key: 'view', label: 'View All' },
                    { key: 'export', label: 'Export' },
                  ] }}
                >
                  <Button type="text" icon={<MoreOutlined />} className="rounded-lg" />
                </Dropdown>
              }
            >
              <div className="space-y-7">
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 bg-blue-500 rounded-full shadow-sm"></div>
                      <span className="text-sm font-medium text-gray-700">In Progress</span>
                    </div>
                    <span className="font-bold text-lg text-blue-600">12</span>
                  </div>
                  <Progress
                    percent={65}
                    strokeColor={{
                      '0%': '#3b82f6',
                      '100%': '#1d4ed8',
                    }}
                    trailColor="#e0e7ff"
                    showInfo={false}
                    size={8}
                    className="rounded-full"
                  />
                </div>

                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 bg-amber-500 rounded-full shadow-sm"></div>
                      <span className="text-sm font-medium text-gray-700">Pending</span>
                    </div>
                    <span className="font-bold text-lg text-amber-600">8</span>
                  </div>
                  <Progress
                    percent={40}
                    strokeColor={{
                      '0%': '#f59e0b',
                      '100%': '#d97706',
                    }}
                    trailColor="#fef3c7"
                    showInfo={false}
                    size={8}
                  />
                </div>

                <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 bg-green-500 rounded-full shadow-sm"></div>
                      <span className="text-sm font-medium text-gray-700">Completed</span>
                    </div>
                    <span className="font-bold text-lg text-green-600">34</span>
                  </div>
                  <Progress
                    percent={85}
                    strokeColor={{
                      '0%': '#10b981',
                      '100%': '#059669',
                    }}
                    trailColor="#d1fae5"
                    showInfo={false}
                    size={8}
                  />
                </div>
              </div>
          </Card>
        </Col>

          {/* Recent Activity */}
          <Col xs={24} lg={16}>
            <Card
              title={
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                    <ClockCircleOutlined className="text-white" />
                  </div>
                  <span className="font-semibold text-gray-800">Recent Activity</span>
                </div>
              }
              style={{
                height: '100%',
                borderRadius: '16px',
                border: 'none',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
              }}
              styles={{ body: { padding: '20px' } }}
              extra={
                <Button
                  type="link"
                  size="small"
                  className="font-medium text-blue-600 hover:text-blue-700 rounded-lg px-4"
                >
                  View All
                </Button>
              }
            >
              <Timeline
                className="mt-6"
                items={recentActivities.map((activity) => ({
                  dot: (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center bg-white border-2 shadow-sm"
                      style={{ borderColor: getStatusColor(activity.status) }}
                    >
                      {getActivityIcon(activity.type, activity.status)}
                    </div>
                  ),
                  color: getStatusColor(activity.status),
                  children: (
                    <div className="ml-2 p-4 bg-gray-50/50 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 mb-2">
                            {activity.title}
                          </div>
                          <div className="text-sm text-gray-600 leading-relaxed">
                            {activity.description}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-xs text-gray-500 font-medium bg-white px-2 py-1 rounded-full">
                            {activity.time}
                          </span>
                          {activity.type === 'customer' && (
                            <Space size="small">
                              <Button
                                type="text"
                                size="small"
                                icon={<PhoneOutlined />}
                                className="text-blue-600 hover:bg-blue-50 rounded-lg"
                              />
                              <Button
                                type="text"
                                size="small"
                                icon={<MessageOutlined />}
                                className="text-green-600 hover:bg-green-50 rounded-lg"
                              />
                            </Space>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                }))}
              />
          </Card>
        </Col>
      </Row>

      </div>
    </div>
  );
};

export default Dashboard;